import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

const SETTINGS = {
  commission_rate: 1.5,
  commission_max_cap: 100,
  commission_description: 'العمولة 1.5% من مبلغ البيع بحد أقصى 100 ر.س',
};

const EXAMPLES = [500, 1500, 3000, 5000, 10000, 20000];

interface Bank {
  id: number;
  bank_name: string;
  account_name: string;
  account_number: string;
  iban: string;
}
interface CommissionRow {
  id: number;
  commission_amount: string;
  status: string;
  payment_date: string | null;
  created_at: string;
  payer_name: string | null;
  transfer_reference: string | null;
  bank_account?: { id: number; bank_name: string } | null;
}

const CSTATUS: Record<string, { label: string; cls: string }> = {
  pending: { label: 'قيد المراجعة', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  paid: { label: 'بانتظار التأكيد', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  confirmed: { label: 'مؤكد', cls: 'bg-green-50 text-green-700 border-green-200' },
  rejected: { label: 'مرفوض', cls: 'bg-red-50 text-red-700 border-red-200' },
};

const formatPrice = (num: number) =>
  num.toLocaleString('ar-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (s: string | null) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('ar-SA', { dateStyle: 'medium' }); }
  catch { return s; }
};

export default function CommissionCalculatorPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuth();

  const [salePrice, setSalePrice] = useState('');
  const [result, setResult] = useState<{ amount: number; rate: number; capped: boolean } | null>(null);

  const [banks, setBanks] = useState<Bank[]>([]);
  const [history, setHistory] = useState<CommissionRow[]>([]);

  // نموذج دفع العمولة
  const [payAmount, setPayAmount] = useState('');
  const [bankId, setBankId] = useState('');
  const [payerName, setPayerName] = useState('');
  const [payDate, setPayDate] = useState('');
  const [reference, setReference] = useState('');
  const [receipt, setReceipt] = useState<File | null>(null);
  const [myAds, setMyAds] = useState<{ id: number; title: string }[]>([]);
  const [payAdId, setPayAdId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [commTab, setCommTab] = useState<'pay' | 'history'>('pay');

  const today = new Date().toISOString().split('T')[0];

  // حاسبة العمولات + الدفع للبائعين فقط
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      toast.error('يجب تسجيل الدخول');
      navigate('/login');
    } else if (user && user.is_seller === false) {
      toast.error('العمولات متاحة للبائعين فقط');
      navigate('/my/seller-profile?redirect=/commission');
    }
  }, [isAuthenticated, isLoading, user, navigate]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get('/my/commissions');
      const p = res.data?.data;
      setHistory(Array.isArray(p) ? p : (p?.data || []));
    } catch { /* تجاهل */ }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || user?.is_seller === false) return;
    api.get('/bank-accounts').then((res) => setBanks(res.data?.data || [])).catch(() => {});
    api.get('/my/ads').then((res) => setMyAds(res.data?.data?.ads || res.data?.data || [])).catch(() => {});
    loadHistory();
  }, [isAuthenticated, user, loadHistory]);

  if (!isLoading && (!isAuthenticated || (user && user.is_seller === false))) {
    return null;
  }

  const calculate = (price?: number) => {
    const value = price || parseFloat(salePrice);
    if (!value || value <= 0) return;
    if (price) setSalePrice(value.toString());
    const raw = (value * SETTINGS.commission_rate) / 100;
    const capped = raw > SETTINGS.commission_max_cap;
    const amount = capped ? SETTINGS.commission_max_cap : raw;
    setResult({ amount, rate: SETTINGS.commission_rate, capped });
  };

  const onReceipt = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setReceipt(f);
  };

  const submitPayment = async () => {
    if (!payAmount || parseFloat(payAmount) <= 0) { toast.error('أدخل المبلغ'); return; }
    if (!bankId) { toast.error('اختر طريقة الدفع (الحساب)'); return; }
    if (!payerName.trim()) { toast.error('أدخل اسم الدافع'); return; }
    if (!payDate) { toast.error('اختر تاريخ الدفع'); return; }
    if (!receipt) { toast.error('أرفق صورة الإيصال'); return; }
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('amount', payAmount);
      fd.append('bank_account_id', bankId);
      fd.append('payer_name', payerName);
      fd.append('payment_date', payDate);
      if (reference.trim()) fd.append('transfer_reference', reference.trim());
      if (payAdId) fd.append('ad_id', payAdId);
      fd.append('receipt_image', receipt);
      await api.post('/commission/pay', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('تم استلام إثبات الدفع، سيتم المراجعة قريباً', { duration: 5000 });
      setPayAmount(''); setBankId(''); setPayerName(''); setPayDate(''); setReference(''); setReceipt(null); setPayAdId('');
      loadHistory();
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      if (errors) {
        const first = Object.values(errors)[0];
        toast.error(Array.isArray(first) ? (first[0] as string) : String(first));
      } else {
        toast.error(err?.response?.data?.message || 'تعذّر إرسال الدفع');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-2" dir="rtl">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-primary-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🧮</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">العمولات</h1>
          <p className="text-gray-500 mt-2">{SETTINGS.commission_description}</p>
        </div>

        {/* التبويبات */}
        <div className="flex gap-2 bg-white border border-gray-200 rounded-2xl p-1.5">
          <button onClick={() => setCommTab('pay')}
            className={`flex-1 h-11 rounded-xl text-sm font-bold transition ${commTab === 'pay' ? 'bg-primary-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
            💳 تسجيل دفع عمولة
          </button>
          <button onClick={() => setCommTab('history')}
            className={`flex-1 h-11 rounded-xl text-sm font-bold transition ${commTab === 'history' ? 'bg-primary-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
            📋 العمولات المدفوعة{history.length > 0 ? ` (${history.length})` : ''}
          </button>
        </div>

        {commTab === 'pay' && (<>
        {/* معلومات */}
        <div className="bg-primary-50 border border-primary-100 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-primary-600 text-xl">ℹ️</span>
            <h3 className="font-bold text-primary-700">كيف تعمل العمولة؟</h3>
          </div>
          <ul className="text-primary-800 text-sm space-y-2 mr-8">
            <li>• العمولة <strong>اختيارية</strong> على ذمة البائع</li>
            <li>• النسبة: <strong>{SETTINGS.commission_rate}%</strong> من مبلغ البيع</li>
            <li>• الحد الأقصى: <strong>{formatPrice(SETTINGS.commission_max_cap)} ر.س</strong></li>
          </ul>
        </div>

        {/* الحاسبة */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <h2 className="font-bold text-gray-900 text-lg mb-4">احسب العمولة</h2>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">مبلغ البيع (ر.س)</label>
            <div className="relative">
              <input type="number" value={salePrice}
                onChange={(e) => { setSalePrice(e.target.value); setResult(null); }}
                onKeyDown={(e) => e.key === 'Enter' && calculate()}
                placeholder="أدخل مبلغ البيع..."
                className="w-full h-14 px-4 pl-16 text-lg border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-left"
                dir="ltr" min="0" />
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">ر.س</span>
            </div>
          </div>
          <div className="mb-5">
            <p className="text-xs text-gray-500 mb-2">أمثلة سريعة:</p>
            <div className="flex flex-wrap gap-2">
              {EXAMPLES.map((price) => (
                <button key={price} onClick={() => calculate(price)}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-primary-50 hover:text-primary-700 text-gray-600 rounded-lg text-sm font-medium transition border border-transparent hover:border-primary-200">
                  {price.toLocaleString('ar-SA')}
                </button>
              ))}
            </div>
          </div>
          <button onClick={() => calculate()} disabled={!salePrice || parseFloat(salePrice) <= 0}
            className="w-full h-12 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl font-bold text-base transition">
            احسب العمولة
          </button>

          {result && (
            <div className="mt-5 bg-primary-50 border border-primary-100 rounded-xl p-5">
              <div className="text-center">
                <p className="text-sm text-primary-700 mb-1">العمولة المطلوبة</p>
                <p className="text-4xl font-bold text-primary-700">{formatPrice(result.amount)}</p>
                <p className="text-primary-600 text-sm mt-1">ر.س</p>
              </div>
              {result.capped && (
                <div className="flex items-center gap-2 text-amber-700 bg-amber-50 rounded-lg p-2 mt-3">
                  <span>⚠️</span>
                  <span className="text-xs">تم تطبيق الحد الأقصى ({formatPrice(SETTINGS.commission_max_cap)} ر.س)</span>
                </div>
              )}
              <button onClick={() => { setPayAmount(String(result.amount)); document.getElementById('pay-form')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="w-full mt-4 h-11 bg-white border-2 border-primary-500 text-primary-600 rounded-xl font-bold text-sm hover:bg-primary-50 transition">
                سجّل دفع هذه العمولة ↓
              </button>
            </div>
          )}
        </div>

        {/* الحسابات البنكية */}
        {banks.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h2 className="font-bold text-gray-900 text-lg mb-4">الحسابات البنكية للتحويل</h2>
            <div className="space-y-3">
              {banks.map((bank) => (
                <div key={bank.id} className="border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center"><span className="text-lg">🏦</span></div>
                    <div>
                      <p className="font-bold text-gray-900">{bank.bank_name}</p>
                      <p className="text-xs text-gray-500">{bank.account_name}</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center bg-gray-50 rounded-lg p-2.5">
                      <span className="text-gray-500">رقم الحساب</span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900" dir="ltr" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: 1, fontVariantNumeric: "tabular-nums" }}>{bank.account_number}</span>
                        <button onClick={() => { navigator.clipboard?.writeText(bank.account_number); toast.success('نُسخ'); }} className="text-primary-600 text-xs" title="نسخ">📋</button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center bg-gray-50 rounded-lg p-2.5">
                      <span className="text-gray-500">IBAN</span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 text-xs" dir="ltr" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: 1, fontVariantNumeric: "tabular-nums" }}>{bank.iban}</span>
                        <button onClick={() => { navigator.clipboard?.writeText(bank.iban); toast.success('نُسخ'); }} className="text-primary-600 text-xs" title="نسخ">📋</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* نموذج دفع العمولة */}
        <div id="pay-form" className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <h2 className="font-bold text-gray-900 text-lg mb-1">تسجيل دفع عمولة</h2>
          <p className="text-xs text-gray-500 mb-5">بعد تحويل العمولة لأحد الحسابات، سجّل بيانات الدفع وأرفق الإيصال ليتم تأكيده.</p>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">المبلغ المدفوع (ر.س) *</label>
                <input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} placeholder="0" min="0" dir="ltr"
                  className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm text-left" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">طريقة الدفع (الحساب المُحوّل إليه) *</label>
                <select value={bankId} onChange={(e) => setBankId(e.target.value)}
                  className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm bg-white">
                  <option value="">اختر الحساب</option>
                  {banks.map((b) => <option key={b.id} value={b.id}>{b.bank_name} — {b.account_name}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">اسم الدافع *</label>
                <input type="text" value={payerName} onChange={(e) => setPayerName(e.target.value)} placeholder="اسم صاحب الحوالة" maxLength={150}
                  className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">تاريخ الدفع *</label>
                <input type="date" value={payDate} max={today} onChange={(e) => setPayDate(e.target.value)}
                  className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">رقم العملية / المرجع <span className="text-gray-400 text-xs font-normal">(اختياري)</span></label>
              <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="رقم مرجع الحوالة إن وجد" maxLength={100} dir="ltr"
                className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm text-left" />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">الإعلان المرتبط <span className="text-gray-400 text-xs font-normal">(اختياري)</span></label>
              <select value={payAdId} onChange={(e) => setPayAdId(e.target.value)}
                className="w-full h-12 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition text-sm bg-white">
                <option value="">بدون تحديد إعلان</option>
                {myAds.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
              </select>
              <p className="text-xs text-gray-400 mt-1">يمكنك الدفع عن نفس الإعلان أكثر من مرة.</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">إيصال العملية *</label>
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-primary-300 transition cursor-pointer"
                onClick={() => document.getElementById('receipt-input')?.click()}>
                {receipt ? (
                  <div>
                    <span className="text-2xl">🧾</span>
                    <p className="text-sm text-primary-600 font-semibold mt-1">{receipt.name}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">اضغط لتغيير الصورة</p>
                  </div>
                ) : (
                  <div>
                    <span className="text-2xl">📸</span>
                    <p className="text-sm text-gray-500 mt-1">اضغط لرفع صورة الإيصال</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">JPG, PNG — حد أقصى 3 ميقا</p>
                  </div>
                )}
              </div>
              <input id="receipt-input" type="file" accept="image/*" onChange={onReceipt} className="hidden" />
            </div>

            <button onClick={submitPayment} disabled={submitting}
              className="w-full h-12 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-xl font-bold text-base transition">
              {submitting ? 'جاري الإرسال...' : 'إرسال إثبات الدفع'}
            </button>
          </div>
        </div>
        </>)}

        {/* سجل مدفوعاتي */}
        {commTab === 'history' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h2 className="font-bold text-gray-900 text-lg mb-4">العمولات المدفوعة</h2>
            {history.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-5xl block mb-3 opacity-60">🧾</span>
                <p className="text-gray-500 text-sm">لا توجد عمولات مدفوعة بعد</p>
                <button onClick={() => setCommTab('pay')}
                  className="mt-4 inline-block px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-xl text-sm font-bold transition">
                  تسجيل دفع عمولة
                </button>
              </div>
            ) : (
              <div className="space-y-2.5">
                {history.map((h) => {
                  const st = CSTATUS[h.status] || CSTATUS.pending;
                  return (
                    <div key={h.id} className="flex items-center justify-between gap-3 border border-gray-200 rounded-xl p-3.5">
                      <div className="min-w-0">
                        <div className="font-bold text-gray-900 text-sm">{formatPrice(parseFloat(h.commission_amount))} ر.س</div>
                        <div className="text-xs text-gray-500 mt-0.5 truncate">
                          {h.bank_account?.bank_name || '—'}
                          <span className="text-gray-300 mx-1.5">•</span>
                          {fmtDate(h.payment_date || h.created_at)}
                          {h.payer_name ? <><span className="text-gray-300 mx-1.5">•</span>{h.payer_name}</> : null}
                        </div>
                      </div>
                      <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border whitespace-nowrap ${st.cls}`}>{st.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="text-center pb-4">
          <button onClick={() => navigate(-1)} className="text-primary-600 hover:text-primary-700 font-semibold text-sm hover:underline">
            ← الرجوع
          </button>
        </div>
      </div>
    </div>
  );
}
